import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { NavBar } from '../components/NavBar';
import './Suppliers.css';

const PIPELINE_STAGES = [
  'Identified',
  'Intro',
  'Vetting',
  'Pilot',
  'Active',
  'At Risk',
  'Dormant',
  'Offboarded'
];

const SUPPLIER_TYPES = ['API', 'Manual', 'API + Manual', 'Panel Build', 'Specialty'];

const GEO_REGIONS = ['US', 'UK', 'Canada', 'Australia', 'EU', 'APAC', 'LATAM', 'MENA', 'Global'];

const SAMPLE_TYPES = ['B2C General Population', 'B2B Decision Makers', 'Healthcare Professionals', 'Hard-to-Reach/Low Incidence', 'Youth/Students', 'Specialty/Niche', 'Multicultural'];

const PAGE_SIZE = 25;

export function Suppliers() {
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({ status: '', type: '', geo: '', sampleType: '' });
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState('created_desc');
  const [page, setPage] = useState(1);

  const navigate = useNavigate();
  const { getToken } = useAuth();

  const filteredSuppliers = suppliers.filter(s => {
    if (filters.sampleType && !(s.sampleTypes || []).includes(filters.sampleType)) return false;
    if (!search) return true;
    const q = search.toLowerCase();
    return s.name.toLowerCase().includes(q) ||
      (s.primaryContactName || '').toLowerCase().includes(q) ||
      (s.hqLocation || '').toLowerCase().includes(q) ||
      (s.notes || '').toLowerCase().includes(q) ||
      (s.companyDescription || '').toLowerCase().includes(q);
  });

  const sortedSuppliers = [...filteredSuppliers].sort((a, b) => {
    if (sort === 'name_asc') return a.name.localeCompare(b.name);
    if (sort === 'name_desc') return b.name.localeCompare(a.name);
    if (sort === 'created_asc') return new Date(a.createdAt) - new Date(b.createdAt);
    return new Date(b.createdAt) - new Date(a.createdAt); // created_desc default
  });

  const totalPages = Math.ceil(sortedSuppliers.length / PAGE_SIZE);
  const paginatedSuppliers = sortedSuppliers.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  useEffect(() => {
    fetchSuppliers();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  useEffect(() => {
    setPage(1);
  }, [search, filters.status, filters.type, filters.geo, filters.sampleType, sort]);

  async function fetchSuppliers() {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      if (filters.type) params.append('type', filters.type);
      if (filters.geo) params.append('geo', filters.geo);

      const response = await fetch(`/api/suppliers?${params.toString()}`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (!response.ok) throw new Error('Failed to fetch suppliers');
      const data = await response.json();
      setSuppliers(data.suppliers);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  function getStageClass(status) {
    const map = {
      'Identified': 'stage-identified',
      'Intro': 'stage-intro',
      'Vetting': 'stage-vetting',
      'Pilot': 'stage-pilot',
      'Active': 'stage-active',
      'At Risk': 'stage-at-risk',
      'Dormant': 'stage-dormant',
      'Offboarded': 'stage-offboarded'
    };
    return map[status] || 'stage-default';
  }

  function getHealthIcon(score) {
    if (score === 'Green') return { icon: '🟢', label: 'Healthy' };
    if (score === 'Yellow') return { icon: '🟡', label: 'Watch' };
    if (score === 'Red') return { icon: '🔴', label: 'At Risk' };
    return { icon: '⚪', label: 'Unrated' };
  }

  function formatDate(dateString) {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  }

  function isOverdue(dateString) {
    if (!dateString) return false;
    return new Date(dateString) < new Date();
  }

  function isDueSoon(dateString) {
    if (!dateString) return false;
    const due = new Date(dateString);
    const soon = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000);
    return due > new Date() && due < soon;
  }

  return (
    <div className="page-container">
      <NavBar />
      <div className="suppliers-container">
      <div className="suppliers-header">
        <h2>Suppliers</h2>
        <button className="button-primary" onClick={() => navigate('/suppliers/new')}>
          + Add Supplier
        </button>
      </div>

      <div className="pipeline-summary">
        {PIPELINE_STAGES.map(stage => {
          const count = suppliers.filter(s => s.status === stage).length;
          return (
            <button
              key={stage}
              className={`pipeline-stage-pill ${getStageClass(stage)} ${filters.status === stage ? 'active' : ''}`}
              onClick={() => setFilters(f => ({ ...f, status: filters.status === stage ? '' : stage }))}
            >
              <span className="stage-name">{stage}</span>
              <span className="stage-count">{count}</span>
            </button>
          );
        })}
      </div>

      <div className="suppliers-search">
        <input
          type="text"
          placeholder="Search by name, contact, location, notes, or description..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="search-input"
        />
        {search && (
          <button className="search-clear" onClick={() => setSearch('')}>✕</button>
        )}
      </div>

      <div className="suppliers-filters">
        <div className="filter-group">
          <label>Type</label>
          <select value={filters.type} onChange={(e) => setFilters({ ...filters, type: e.target.value })}>
            <option value="">All Types</option>
            {SUPPLIER_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <div className="filter-group">
          <label>Geography</label>
          <select value={filters.geo} onChange={(e) => setFilters({ ...filters, geo: e.target.value })}>
            <option value="">All Regions</option>
            {GEO_REGIONS.map(g => <option key={g} value={g}>{g}</option>)}
          </select>
        </div>
        <div className="filter-group">
          <label>Sample Type</label>
          <select value={filters.sampleType} onChange={e => setFilters({ ...filters, sampleType: e.target.value })}>
            <option value="">All Sample Types</option>
            {SAMPLE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <div className="filter-group">
          <label>Sort By</label>
          <select value={sort} onChange={e => setSort(e.target.value)}>
            <option value="created_desc">Newest First</option>
            <option value="created_asc">Oldest First</option>
            <option value="name_asc">Name A → Z</option>
            <option value="name_desc">Name Z → A</option>
          </select>
        </div>
        <button className="filter-clear" onClick={() => setFilters({ status: '', type: '', geo: '', sampleType: '' })}>
          Clear Filters
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}

      {loading ? (
        <div className="loading">Loading suppliers...</div>
      ) : (
        <div className="suppliers-table-container">
          <table className="suppliers-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Stage</th>
                <th>Health</th>
                <th>Type</th>
                <th>Account Manager</th>
                <th>Last Activity</th>
                <th>Next Action</th>
              </tr>
            </thead>
            <tbody>
              {paginatedSuppliers.length === 0 ? (
                <tr>
                  <td colSpan="7" className="no-results">
                    No suppliers found. {search
                      ? 'Try a different search term.'
                      : filters.status || filters.type || filters.geo
                      ? 'Try adjusting your filters.'
                      : 'Add your first supplier to get started.'}
                  </td>
                </tr>
              ) : (
                paginatedSuppliers.map(supplier => {
                  const health = getHealthIcon(supplier.healthScore);
                  return (
                    <tr
                      key={supplier.id}
                      onClick={() => navigate(`/suppliers/${supplier.id}`)}
                      className="supplier-row"
                    >
                      <td className="supplier-name">{supplier.name}</td>
                      <td>
                        <span className={`stage-badge ${getStageClass(supplier.status)}`}>
                          {supplier.status}
                        </span>
                      </td>
                      <td>
                        <span className="health-icon" title={health.label}>
                          {health.icon}
                        </span>
                      </td>
                      <td>{supplier.supplierType || '—'}</td>
                      <td>{supplier.accountManager || '—'}</td>
                      <td>{supplier.lastActivityDate ? `${formatDate(supplier.lastActivityDate)}${supplier.lastActivityType ? ` · ${supplier.lastActivityType}` : ''}` : '—'}</td>
                      <td className={
                        isOverdue(supplier.nextActionDate) ? 'checkin-overdue' :
                        isDueSoon(supplier.nextActionDate) ? 'checkin-soon' : ''
                      }>
                        {formatDate(supplier.nextActionDate)}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      )}

      {totalPages > 1 && (
        <div className="pagination">
          <button className="page-btn" onClick={() => setPage(p => p - 1)} disabled={page === 1}>← Prev</button>
          {Array.from({ length: totalPages }, (_, i) => i + 1).map(n => (
            <button key={n} className={`page-btn ${n === page ? 'active' : ''}`} onClick={() => setPage(n)}>{n}</button>
          ))}
          <button className="page-btn" onClick={() => setPage(p => p + 1)} disabled={page === totalPages}>Next →</button>
        </div>
      )}

      <div className="suppliers-summary">
        {sortedSuppliers.length === 0
          ? 'No suppliers found'
          : `Showing ${(page - 1) * PAGE_SIZE + 1}–${Math.min(page * PAGE_SIZE, sortedSuppliers.length)} of ${sortedSuppliers.length} supplier${sortedSuppliers.length !== 1 ? 's' : ''}${filters.status ? ` in ${filters.status}` : ''}`
        }
      </div>
    </div>
    </div>
  );
}
